import { Component, computed, inject } from '@angular/core';
import { MACHINE_DEFS, isJoinMachine } from '@sim/content/machine-defs';
import { MATERIALS } from '@sim/content/materials';
import type { MachineEntity, SourceSpec } from '@sim/core/entities';
import { findEntityAt, findSource } from '@sim/core/grid';
import { BuildToolService } from '@app/core/services/build-tool.service';
import { UiStateService } from '@app/core/services/ui-state.service';
import { SimEngineService } from '@app/core/services/sim-engine.service';
import { MaterialIconComponent } from '@app/shared/material-icon.component';

@Component({
  selector: 'app-status-strip',
  imports: [MaterialIconComponent],
  template: `
    <div
      class="flex min-h-9 shrink-0 flex-wrap items-center gap-x-2 gap-y-1 border-b border-slate-800 bg-slate-900 px-4 py-1.5 text-xs text-slate-400"
    >
      @if (hoveringFrozenFunction()) {
        <span class="font-semibold text-sky-300">Live subscription</span>
        <span>Pull the plug before changing what the Subscriber is built from</span>
      } @else if (selectedMachine(); as machine) {
        <span class="font-semibold text-sky-300">{{ MACHINE_DEFS[machine.kind].label }}</span>
        <span>{{ MACHINE_DEFS[machine.kind].description }}</span>
        @if (needsConfig(machine)) {
          <span class="font-semibold text-amber-400"
            >configure it on the map to start the flow</span
          >
        }
      } @else if (hoveredSource(); as source) {
        <span
          class="font-semibold"
          [class.text-teal-300]="!hasByproduct(source)"
          [class.text-purple-300]="hasByproduct(source)"
        >
          Source
        </span>
        <span>{{ summary(source) }}</span>
        <span class="flex gap-1">
          @for (yield of source.yields; track yield.material) {
            <app-material-icon [material]="yield.material" [size]="18" />
          }
        </span>
        <span [class.text-amber-400]="source.extracted >= source.capacity">
          {{ source.capacity - source.extracted }} of {{ source.capacity }} left this subscription
        </span>
      } @else {
        <span>Hover a source to preview its yield, or select a built operator to configure it</span>
      }
    </div>
  `,
})
export class StatusStripComponent {
  private readonly ui = inject(UiStateService);
  private readonly engine = inject(SimEngineService);
  private readonly tools = inject(BuildToolService);
  protected readonly MACHINE_DEFS = MACHINE_DEFS;

  protected readonly selectedMachine = computed<MachineEntity | null>(() => {
    const pos = this.tools.selectedCell();
    if (!pos) return null;
    const entity = findEntityAt(this.engine.state(), pos);
    if (!entity || entity.kind !== 'machine') return null;
    return this.engine.state().machines[entity.id];
  });

  /** A join has nothing to pick — it takes streams, not arguments. */
  protected needsConfig(machine: MachineEntity): boolean {
    return !isJoinMachine(machine) && machine.config === null;
  }

  protected readonly hoveringFrozenFunction = computed(() => {
    const pos = this.ui.hoveredPos();
    return pos !== null && this.engine.isSubscriberSlotFrozen(pos);
  });

  protected readonly hoveredSource = computed(() => {
    const pos = this.ui.hoveredPos();
    if (!pos) return null;
    return findSource(this.engine.state(), pos) ?? null;
  });

  protected hasByproduct(source: SourceSpec): boolean {
    return source.yields.length > 1;
  }

  protected summary(source: SourceSpec): string {
    const total = source.yields.reduce((sum, y) => sum + y.weight, 0);
    return source.yields
      .map((y) =>
        this.hasByproduct(source)
          ? `${Math.round((y.weight / total) * 100)}% ${MATERIALS[y.material].label}`
          : MATERIALS[y.material].label,
      )
      .join(', ');
  }
}
